/**
 * Hyundai Smart Search — Frontend v1.12
 *
 * Changes in v1.12:
 *  - state now carries `sort` alongside year/model/trim/feature/keywords
 *    (see hss-core.js v1.8). This bar has no sort UI of its own — that's
 *    the new [hss_video_sort] shortcode — but Search and Clear All now
 *    read/clear it too, so submitting a chip change no longer silently
 *    drops whatever sort was already applied, and Clear All resets sort
 *    along with everything else. _syncFromFacet() also keeps state.sort
 *    current off 'facetwp-loaded' so a sort change made elsewhere on the
 *    page (the sort control itself) isn't clobbered by a later Search
 *    from this bar.
 *
 * Changes in v1.11:
 *  - The single "Feature category matching" toggle is now two separate
 *    ones (Settings → Search Behaviour): "…— autocomplete"
 *    (featureDropdownEnabled) covers everything the dropdown itself
 *    shows — typed-text matches alongside Model/Trim results, and the
 *    guided Feature Category step after Year+Model+Trim are picked —
 *    while "…— Smart Parse" (featureMatchingEnabled, the original
 *    toggle) still only covers the typed-text-to-feature GUESS on
 *    Enter/Search. Lets the team turn feature_category matching on in
 *    the dropdown to test it without also turning on the riskier
 *    auto-guess-on-submit behavior, or the reverse.
 *
 * Changes in v1.10:
 *  - The Year+Model contextual case never checked whether a Trim was
 *    ALSO already selected, so after picking a trim, re-focusing the
 *    input kept re-showing the exact same trim list forever instead of
 *    moving on. _getResults() now has two more specific cases ahead of
 *    it: Year+Model+Trim (no Feature yet) previews Feature Category
 *    options as the next guided step; Year+Model+Trim+Feature (every
 *    guided facet picked) previews nothing at all, since a keyword
 *    search is the only thing left and that isn't a browsable list. The
 *    Feature Category preview step still respects the existing
 *    "Feature category matching" admin toggle that already gates
 *    smart-parse's typed-text guessing — that toggle stays untouched:
 *    it's still only about text getting silently reinterpreted as a
 *    filter on Enter/Search, never about clicking an option this
 *    dropdown is explicitly offering at this step.
 *
 * Changes in v1.9:
 *  - Cascade preview: typing a complete Year (e.g. "2026") without
 *    actually selecting it as a chip now also shows a second group below
 *    it — "Models for 2026" — previewing every model under that year.
 *    Same idea one level deeper: an unambiguous single Model match
 *    previews that model's Trims. Only fires when the match is genuinely
 *    unambiguous (exactly one Year or Model result) and isn't already an
 *    active chip. See HSSBar.prototype._addCascadePreview().
 *  - Fixed: selecting a chip from the dropdown didn't immediately show
 *    the next-level preview (e.g. picking "2026" wouldn't show Models
 *    for 2026 until you tabbed out and back in, or started typing).
 *    Root cause was the document-level "outside click closes dropdown"
 *    handler using $wrap.contains(e.target) — _selectResult() removes
 *    the very element that was just clicked (via _hideDropdown(), en
 *    route to rendering the next-level dropdown) before this same click
 *    finishes bubbling to document, so by the time the outside-click
 *    check ran, e.target was already a detached node and .contains()
 *    always reported "outside," closing the fresh dropdown that had
 *    literally just appeared in the same click. Switched to
 *    e.composedPath(), which reflects the DOM as it existed when the
 *    event was dispatched and isn't fooled by mutations mid-bubble.
 *  - Search button / Enter now shows a brief loading overlay when they
 *    trigger a real page redirect (see HSSCore.navigate() in
 *    hss-core.js) instead of leaving a dead blank moment between the
 *    click and the results page finishing its load.
 *
 * Changes in v1.8.11:
 *  - Removing a chip (×) no longer searches instantly — it used to be the
 *    one action in this bar that applied itself immediately, while picking
 *    a result from the dropdown just staged it until Search/Enter. Chip
 *    removal now stages too, matching the configurator: nothing hits
 *    FacetWP until Search is clicked or Enter is pressed. Clear All is
 *    unchanged — it's still an explicit, immediate action on both the bar
 *    and the configurator.
 *
 * Fixes in v1.8.10:
 *  - The [hyundai_smart_search search_page="…"] per-instance override was
 *    parsed by the shortcode but never actually read anywhere — hssConfig
 *    (and its searchPage) is localized once, globally, on
 *    wp_enqueue_scripts, which runs before the shortcode's own atts exist.
 *    Boot now reads data-search-page off #hss-search-wrap (printed by
 *    search-bar.php when the attribute is set) and overrides searchPage
 *    for this instance only; the admin settings URL remains the default
 *    whenever no override is given.
 *
 * Fixes in v1.8.9:
 *  - Smart Parse's Model and Trim extraction only ever looked at the text
 *    typed in that specific search, requiring the level above to be
 *    retyped every time (e.g. "2026 Ioniq 5" worked, but typing just
 *    "Ioniq 5" with a 2026 Year chip already active did not — it fell
 *    through to a plain keyword search instead of a Model chip, same one
 *    level deeper for Trim with Year+Model already active). Extraction
 *    now falls back to the currently active Year/Model chip when the
 *    corresponding text isn't retyped. Retyping a year explicitly still
 *    resets Model/Trim, same as before.
 *
 * Changes in v1.8.8:
 *  - _smartParseAndSearch() now runs the typed text through
 *    HSSCore.applyTermCorrections() first (Settings → Term Corrections),
 *    so a known misspelling/alias is fixed before Year/Model/Trim/Feature
 *    extraction — the intercept applies on both Enter and the Search
 *    button, since both funnel through this one function.
 *
 * Fixes in v1.8.7:
 *  - The v1.8.6 background fetch() to FWPVFM's clear endpoint wasn't
 *    reliably clearing its cookie — whatever it's doing on
 *    template_redirect seems to depend on a real page load. Reverted to
 *    a genuine window.location.href navigation, but to the current page
 *    (window.location.pathname + search) instead of config.searchPage,
 *    so it reloads in place rather than sending the user to the results
 *    page. See _clearAll()'s docblock.
 *
 * Fixes in v1.8.6:
 *  - Clear All / the × clear-all button was leaving the page (a full
 *    window.location.href redirect to FWPVFM's ?_clear_vehicle_filter=1
 *    endpoint) whenever the bar wasn't on the actual FacetWP results
 *    page. It now stays put: chips clear locally as before, and FWPVFM's
 *    cookie is cleared via a background fetch() to that same endpoint
 *    instead of sending the browser there. See _clearAll()'s docblock.
 *  - .hss-inner (the chip + input row) switched from overflow:hidden to
 *    a horizontal scroll. With several chips active the oldest ones —
 *    and their × remove button — could get clipped past the visible
 *    edge with no way to reach them; see hss-frontend.css.
 *
 * Fixes in v1.8.5:
 *  - Clear (×) wasn't actually clearing off the results page: a plain
 *    clean-URL navigate runs straight into FWPVFM's own "homepage, no
 *    filter param, but a saved cookie exists -> reapply it" redirect,
 *    since we stopped writing/clearing that cookie ourselves in v1.8.4.
 *    Now routes through FWPVFM's own ?_clear_vehicle_filter=1 endpoint
 *    when off the results page (same fix as the configurator's Clear All).
 *  - Feature Category now persists across pages too, via its own small
 *    cookie (HSSCore.syncFeatureCookie/readFeatureCookie) — FWPVFM's
 *    cookie is deliberately year/model/trim only, so Feature needed its
 *    own separate storage rather than changing another plugin's format.
 *
 * Changes in v1.8.4:
 *  - Stopped writing the FWPVFM cookie ourselves (HSSCore.syncFWPVFMCookie
 *    removed — see hss-core.js changelog). FWPVFM already keeps it in sync
 *    via its own native 'facetwp-loaded' listener, which _navigate() was
 *    already feeding correctly the whole time via pushState. Two systems
 *    writing the same cookie was redundant at best, a race at worst.
 *
 * Changes in v1.8.3:
 *  - Both this bar and the configurator are placed globally (header +
 *    top-of-page feed), so most pages have no _model_filter in their own
 *    URL to restore from. Init now calls HSSCore.resolveInitialState()
 *    instead of parseUrlState() directly: URL wins whenever it has
 *    anything (results page, deep links), falling back to the FWPVFM
 *    cookie everywhere else. The facetwp-loaded sync handler deliberately
 *    still uses parseUrlState() directly — it only ever fires when
 *    FacetWP itself is live, so the URL it just pushed is authoritative.
 *
 * Changes in v1.8.2:
 *  - Search button no longer shows a magnifying-glass icon, just the
 *    "Search" text (the icon in the input box itself is unchanged).
 *  - FWPVFM cookie sync/clear moved into HSSCore (syncFWPVFMCookie /
 *    clearFWPVFMCookie) so the configurator can share the exact same
 *    logic instead of only the chip bar having it.
 *
 * Fix in v1.8.1:
 *  - Model preview thumbnails were failing to load. Two bugs, both in
 *    _loadModelImages/_applyModelImage:
 *     1. Read `data.url` from the REST response, but HSS_Model_Image
 *        (includes/class-hss-model-image.php) has always responded with
 *        `image`, not `url` — so the real photo URL was silently discarded
 *        every time in favour of the (usually empty) fallback.
 *     2. Switched from injecting an <img> to setting el.style.backgroundImage,
 *        but hss-frontend.css only ever styled a child `.hss-result-thumb img`
 *        (no background-size/position was ever added for the other approach)
 *        — and on failure the whole thumb box got display:none'd, which
 *        reflows the result row out from under the cursor right as someone
 *        goes to click it. Reverted to building a real <img> tag, and a
 *        failed/missing image now just leaves the empty gray placeholder in
 *        place instead of collapsing the box.
 *  - Also restored the modelImage.enabled/restUrl guard clause and the
 *    dropdown-was-already-replaced (el.isConnected) guard that got dropped
 *    in the same edit — neither caused the reported symptom directly, but
 *    both are real robustness regressions worth restoring.
 *  - Reviewed the rest of the v1.8.0 refactor (HSSCore extraction, Feature
 *    Category support) end-to-end — including live execution tests of
 *    typing, selecting a result, removing a chip, Search, and Clear — and
 *    didn't find a fault in that logic itself; the image bug's layout shift
 *    is the most likely source of anything that felt like broken search/
 *    clear behaviour, since it visibly reflows result rows right as you'd
 *    go to click one.
 *
 * Changes in v1.8.0:
 *  - buildFlat / score / fuzzySubseq / searchFlat / URL build+parse / FWP
 *    navigation moved OUT of this file and into the shared assets/js/hss-core.js
 *    module (window.HSSCore), so this chip bar and the new visual configurator
 *    (see class-hss-configurator.php) both call one canonical implementation.
 *    This file now only owns DOM/interaction concerns for the chip bar itself.
 *  - Added a fourth, independent filter dimension: Feature Category
 *    (feature_category taxonomy — flat, no year/model/trim ancestry). Shows
 *    up as its own dropdown result group, its own chip colour, and its own
 *    URL param (paramFeature, default _feature_category) alongside the
 *    existing paramModel/paramKeyword params — e.g.
 *    ?_model_filter=2026,elantra-2026&_feature_category=driving
 *
 * (See previous version history in git — trimmed here for brevity.)
 */
(function () {
	'use strict';

	/* ─── State ─────────────────────────────────────────────────────── */
	// sort has no chip UI of its own here (that's [hss_video_sort]'s job) —
	// it's carried purely so Search/Clear All don't drop whatever sort is
	// already applied. See hss-core.js v1.8.
	var state = { year: null, model: null, trim: null, feature: null, keywords: '', sort: null };

	/* ─── Main class ─────────────────────────────────────────────────── */
	function HSSBar(config, tree) {
		this.config        = config;
		this.tree          = tree;
		this.debounceTimer = null;
	}

	HSSBar.prototype.init = function () {
		this.$wrap = document.getElementById('hss-search-wrap');
		if (!this.$wrap) return;

		this.$input     = this.$wrap.querySelector('#hss-input');
		this.$chips     = this.$wrap.querySelector('#hss-chips');
		this.$dropdown  = this.$wrap.querySelector('#hss-dropdown');
		this.$searchBtn = this.$wrap.querySelector('#hss-search-btn');
		this.$clearBtn  = this.$wrap.querySelector('#hss-clear-btn');

		HSSCore.buildFlat(this.tree, window.hssFeatureCategories);
		HSSCore.setFeatureMatchingEnabled(this.config.featureMatchingEnabled);
		HSSCore.setFeatureDropdownEnabled(this.config.featureDropdownEnabled);
		HSSCore.setTermCorrections(window.hssTermCorrections);
		// URL wins when present (results page, or a shared deep link);
		// falls back to the FWPVFM cookie everywhere else, since this bar
		// appears globally in the header and has no _model_filter of its
		// own to read on most pages.
		Object.assign(state, HSSCore.resolveInitialState(this.config));
		this._renderChips();
		this._bindEvents();
	};

	/* ── Event binding ───────────────────────────────────────────────── */
	HSSBar.prototype._bindEvents = function () {
		var self = this;

		/* Input: debounced suggestions */
		this.$input.addEventListener('input', function () {
			clearTimeout(self.debounceTimer);
			var val = self.$input.value.trim();
			// Even empty input shows contextual suggestions if chips are set
			self.debounceTimer = setTimeout(function () {
				self._showSuggestions(val);
			}, 220);
		});

		/* Focus: show contextual suggestions immediately when chips exist */
		this.$input.addEventListener('focus', function () {
			var hasContext = !!(state.year || state.model);
			if (hasContext && !self.$input.value.trim()) {
				self._showSuggestions('');
			}
		});

		/* Keyboard navigation */
		this.$input.addEventListener('keydown', function (e) {
			if (e.key === 'Enter') {
				e.preventDefault();
				self._smartParseAndSearch(self.$input.value.trim());
			}
			if (e.key === 'Escape')   { self._hideDropdown(); }
			if (e.key === 'ArrowDown') {
				e.preventDefault();
				var first = self.$dropdown.querySelector('.hss-result');
				if (first) first.focus();
			}
		});

		/* Search button */
		this.$searchBtn.addEventListener('click', function () {
			var val = self.$input.value.trim();
			if (val) self._smartParseAndSearch(val);
			else     self._navigate();
			// Some browsers focus a <button> on mouse click and leave it
			// focused indefinitely. Since the button lives inside
			// .hss-outer, that keeps :focus-within — and the idle→focused
			// colour swap — permanently "stuck" on the focused colour even
			// after the search has run. Blur it so idle styling returns.
			self.$searchBtn.blur();
		});

		/* Clear button */
		this.$clearBtn.addEventListener('click', function () {
			self._clearAll();
			self.$clearBtn.blur();   // see note above on .hss-search-btn
		});

		/* Chip removal (delegated) */
		this.$chips.addEventListener('click', function (e) {
			var btn = e.target.closest('.hss-chip-remove');
			if (btn) {
				self._removeChip(btn.dataset.type);
				btn.blur();   // see note above on .hss-search-btn
			}
		});

		/* Outside click closes dropdown.
		 *
		 * Deliberately uses e.composedPath() instead of
		 * self.$wrap.contains(e.target). Selecting a result
		 * (_selectResult → _hideDropdown) removes the very element that
		 * was just clicked from the DOM as part of committing the chip
		 * — but that all happens *before* this same click event finishes
		 * bubbling up to document. By the time this handler runs,
		 * e.target is a detached node, and .contains() on a detached
		 * node always reports "outside" — even though the click
		 * genuinely landed inside the widget — which was closing the
		 * next-level preview dropdown that _selectResult()'s own
		 * this.$input.focus() call had *just* triggered via the focus
		 * listener below, all within that same click. composedPath()
		 * captures the ancestor chain as it existed at dispatch time, so
		 * later DOM mutations mid-bubble can't fool it.
		 */
		document.addEventListener('click', function (e) {
			var path = (typeof e.composedPath === 'function') ? e.composedPath() : null;
			var inside = path ? path.indexOf(self.$wrap) !== -1 : self.$wrap.contains(e.target);
			if (!inside) self._hideDropdown();
		});

		/* ── FacetWP sync ────────────────────────────────────────────────
		 * facetwp-loaded fires after every FWP AJAX cycle — whether
		 * triggered by the native facet dropdowns OR by our own _navigate().
		 * We re-read the URL (FWP has already updated it) and repaint chips.
		 * We deliberately do NOT call _navigate() / FWP.fetch() here, so
		 * the listener is strictly one-way: FacetWP state → chip display.
		 * ---------------------------------------------------------------- */
		if (window.jQuery) {
			window.jQuery(document).on('facetwp-loaded', function () {
				self._syncFromFacet();
			});
		}
	};

	/* ── Cascade preview ──────────────────────────────────────────────── */
	/**
	 * Completing a Year or Model unambiguously — the typed text resolves
	 * to exactly one match — but WITHOUT actually selecting it as a chip,
	 * also previews that match's children one level down: type "2026"
	 * and, alongside the single "2026" Year result, a second group
	 * appears below it (separated by the existing .hss-group border, plus
	 * a muted/italic label — see hss-frontend.css) listing every Model
	 * for 2026, e.g. "Models for 2026". Same idea one level deeper: an
	 * unambiguous single Model match previews that model's Trims.
	 *
	 * Deliberately read-only — nothing here touches `state`. Clicking a
	 * previewed row commits it exactly like any other result via the
	 * normal _selectResult() flow, since preview rows carry the same
	 * data-type/data-slug(+year/model) attributes as a real match; only
	 * the source of the array differs (every child, not a text-scored
	 * subset), because there's no leftover query text to score them
	 * against once it's already been consumed matching the parent.
	 *
	 * Only relevant when the match in question isn't already an active
	 * chip — if state.year (or state.model) is already set, its children
	 * are already the normal result set, not a preview of one.
	 *
	 * @param  {Object} results  Mutated in place AND returned for chaining.
	 * @return {Object} results
	 */
	HSSBar.prototype._addCascadePreview = function (results) {
		if (!state.year && results.years.length === 1) {
			var yearNode = this.tree.find(function (y) { return y.slug === results.years[0].slug; });
			if (yearNode && (yearNode.models || []).length) {
				results.previewModels = yearNode.models.map(function (m) {
					return Object.assign({}, m, { yearSlug: yearNode.slug, yearName: yearNode.name });
				});
				results.previewModelsLabel = 'Models for ' + yearNode.name;
			}
		}

		if (!state.model && results.models.length === 1) {
			var modelSlug = results.models[0].slug;
			var modelNode = null, parentYear = null;
			this.tree.some(function (y) {
				var found = (y.models || []).find(function (m) { return m.slug === modelSlug; });
				if (found) { modelNode = found; parentYear = y; return true; }
				return false;
			});
			if (modelNode && (modelNode.trims || []).length) {
				results.previewTrims = modelNode.trims.map(function (t) {
					return Object.assign({}, t, {
						modelSlug: modelNode.slug, modelName: modelNode.name,
						yearSlug: parentYear.slug, yearName: parentYear.name,
					});
				});
				results.previewTrimsLabel = 'Trims for ' + modelNode.name;
			}
		}

		return results;
	};

	/* ── Contextual (filtered) result builder ────────────────────────── */
	/**
	 * Returns suggestions scoped to how far the guided Year → Model →
	 * Trim → Feature Category chain has gotten:
	 *   - year + model + trim + feature ALL selected → nothing left to
	 *     preview; every guided facet is picked, so from here it's a
	 *     plain free-text keyword search (see the terminal case below)
	 *   - year + model + trim selected (feature not yet) → only Feature
	 *     Category options remain as the next guided step
	 *   - year + model selected (trim not yet)  → only trims for that model
	 *   - year selected only                     → only models for that year (+ matching trims)
	 *   - nothing selected                       → full search via HSSCore.searchFlat (query required)
	 *
	 * The order below matters: each case's condition is a strict subset
	 * of the one above it (e.g. "+trim" implies "+model" implies "+year"),
	 * so the more specific cases must be checked — and return — first.
	 */
	HSSBar.prototype._getResults = function (query) {
		var q = (query || '').toLowerCase().trim();
		var results = { years: [], models: [], trims: [], features: [] };
		var flat = HSSCore.getFlat();

		function matchFeatures() {
			if (!q || !HSSCore.isFeatureDropdownEnabled()) return;
			flat.features.forEach(function (f) {
				var s = HSSCore.score(f.name, q);
				if (s > 0) results.features.push(Object.assign({}, f, { _score: s }));
			});
			results.features.sort(function (a, b) { return b._score - a._score; });
		}

		/* ── TERMINAL: Year + Model + Trim + Feature all selected ──────
		 * Every guided facet has been picked explicitly — the only thing
		 * left is a plain keyword search, which isn't a browsable list,
		 * so there's nothing to preview. Empty results here means
		 * _showSuggestions()'s hasAny check fails and the dropdown simply
		 * doesn't open, regardless of what (if anything) gets typed. */
		if (state.year && state.model && state.trim && state.feature) {
			return results;
		}

		/* ── CASE 0: Year + Model + Trim selected (Feature not yet) →
		 * only Feature Category options remain as the guided next step.
		 * Gated by featureDropdownEnabled (Settings → "Feature category
		 * matching — autocomplete") — the toggle that governs the
		 * dropdown itself, kept deliberately separate from
		 * featureMatchingEnabled (Settings → "…— Smart Parse"), which
		 * only gates the typed-text-to-feature GUESS on Enter/Search.
		 * This case is the user explicitly clicking one of the options
		 * this dropdown is offering at this step — never free text being
		 * silently reinterpreted as a filter — so it was worth being
		 * independently toggleable, e.g. to try it on staging without it
		 * going live on prod. */
		if (state.year && state.model && state.trim) {
			if (HSSCore.isFeatureDropdownEnabled()) {
				flat.features.forEach(function (f) {
					// All features when empty; score when query exists
					var s = q ? HSSCore.score(f.name, q) : 75;
					if (s > 0) results.features.push(Object.assign({}, f, { _score: s }));
				});
				results.features.sort(function (a, b) { return b._score - a._score; });
			}
			return results;
		}

		/* ── CASE 1: Year + Model selected (Trim not yet) → only trims for this model ── */
		if (state.year && state.model) {
			var yearNode  = this.tree.find(function (y) { return y.slug === state.year.slug; });
			if (!yearNode) { matchFeatures(); return results; }
			var modelNode = (yearNode.models || []).find(function (m) { return m.slug === state.model.slug; });
			if (!modelNode) { matchFeatures(); return results; }

			(modelNode.trims || []).forEach(function (trim) {
				// Show all trims when empty; score when query exists
				var s = q ? HSSCore.score(trim.name, q) : 75;
				if (s > 0) {
					results.trims.push(Object.assign({}, trim, {
						modelSlug: modelNode.slug,
						modelName: modelNode.name,
						yearSlug:  yearNode.slug,
						yearName:  yearNode.name,
						_score:    s,
					}));
				}
			});
			results.trims.sort(function (a, b) { return b._score - a._score; });
			matchFeatures();
			return results;
		}

		/* ── CASE 2: Year selected → only models (and matching trims) for this year ── */
		if (state.year) {
			var yearNode = this.tree.find(function (y) { return y.slug === state.year.slug; });
			if (!yearNode) { matchFeatures(); return results; }

			(yearNode.models || []).forEach(function (model) {
				// All models when empty; score when query exists
				var ms = q ? HSSCore.score(model.name, q) : 75;
				if (ms > 0) {
					results.models.push(Object.assign({}, model, {
						yearSlug: yearNode.slug,
						yearName: yearNode.name,
						_score:   ms,
					}));
				}
				// Only show trims when user is actively typing something
				if (q) {
					(model.trims || []).forEach(function (trim) {
						var ts = HSSCore.score(trim.name, q);
						if (ts > 0) {
							results.trims.push(Object.assign({}, trim, {
								modelSlug: model.slug,
								modelName: model.name,
								yearSlug:  yearNode.slug,
								yearName:  yearNode.name,
								_score:    ts,
							}));
						}
					});
				}
			});

			results.models.sort(function (a, b) { return b._score - a._score; });
			results.trims.sort(function (a, b) { return b._score - a._score; });
			matchFeatures();
			return this._addCascadePreview(results);
		}

		/* ── CASE 3: No context → full search across everything (requires query) ── */
		return this._addCascadePreview(HSSCore.searchFlat(q));
	};

	/* ── Dropdown renderer ───────────────────────────────────────────── */
	HSSBar.prototype._showSuggestions = function (query) {
		var results = this._getResults(query);
		var self    = this;
		var colors  = this.config.chipColors;
		var hasAny  = results.years.length || results.models.length || results.trims.length || results.features.length;

		if (!hasAny) { this._hideDropdown(); return; }

		var showThumbs = !!(this.config.modelImage && this.config.modelImage.enabled);

		var html = '<div class="hss-dropdown-inner">';

		function group(label, items, type, inlineMetaFn, extraClass, badgeLabel) {
			if (!items.length) return '';
			var g = '<div class="hss-group' + (extraClass ? ' ' + extraClass : '') + '">'
				  + '<div class="hss-group-label">' + HSSCore.escHtml(label) + '</div>';
			items.slice(0, 7).forEach(function (item) {
				g += '<div class="hss-result" role="option" tabindex="0"'
				   + ' data-type="'       + type                        + '"'
				   + ' data-slug="'       + HSSCore.escHtml(item.slug)          + '"'
				   + ' data-name="'       + HSSCore.escHtml(item.name)          + '"'
				   + ' data-year-slug="'  + HSSCore.escHtml(item.yearSlug  || '') + '"'
				   + ' data-year-name="'  + HSSCore.escHtml(item.yearName  || '') + '"'
				   + ' data-model-slug="' + HSSCore.escHtml(item.modelSlug || '') + '"'
				   + ' data-model-name="' + HSSCore.escHtml(item.modelName || '') + '">';
				// Preview thumbnail — Model-level results only, never Trim/Year/Feature.
				if (showThumbs && type === 'model') {
					g += '<span class="hss-result-thumb" data-preview-slug="' + HSSCore.escHtml(item.slug) + '"></span>';
				}
				g += '<span class="hss-badge" style="background:' + HSSCore.escHtml(colors[type]) + '">'
				   +   HSSCore.escHtml(badgeLabel || label)
				   + '</span>'
				   + '<span class="hss-result-name">' + HSSCore.highlight(item.name, query);
				// Passive selectors (year for Model; model + year for Trim) render
				// inline right after the name — never pinned to the far right —
				// so the active + passive context reads as one unit.
				if (inlineMetaFn) {
					var im = inlineMetaFn(item);
					if (im) g += '<span class="hss-result-year">' + HSSCore.escHtml(im) + '</span>';
				}
				g += '</span>';
				g += '</div>';
			});
			g += '</div>';
			return g;
		}

		html += group('Year',    results.years,    'year');
		html += group('Model',   results.models,   'model', function (m) { return m.yearName; });
		html += group('Trim',    results.trims,    'trim',  function (t) {
			return state.model ? '' : (t.modelName + ' • ' + t.yearName);
		});
		html += group('Feature', results.features, 'feature');

		// Cascade preview groups — see _addCascadePreview()'s docblock.
		// Rendered with the 'hss-group-preview' modifier (muted/italic
		// label; see hss-frontend.css) so they read as "here's a preview
		// of what's below" rather than a second, confusingly-duplicate
		// search-match group. badgeLabel keeps each row's small colored
		// badge reading just "Model"/"Trim" — the longer "Models for
		// 2026" heading belongs on the group label only.
		if (results.previewModels && results.previewModels.length) {
			html += group(results.previewModelsLabel, results.previewModels, 'model',
				function (m) { return m.yearName; }, 'hss-group-preview', 'Model');
		}
		if (results.previewTrims && results.previewTrims.length) {
			html += group(results.previewTrimsLabel, results.previewTrims, 'trim',
				function (t) { return t.modelName + ' • ' + t.yearName; }, 'hss-group-preview', 'Trim');
		}

		html += '</div>';

		this.$dropdown.innerHTML  = html;
		this.$dropdown.style.display = 'block';

		if (showThumbs) this._loadModelImages();

		/* Bind result interactions */
		this.$dropdown.querySelectorAll('.hss-result').forEach(function (el) {
			el.addEventListener('click', function () { self._selectResult(el); });
			el.addEventListener('keydown', function (e) {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					self._selectResult(el);
				}
				if (e.key === 'ArrowDown') {
					e.preventDefault();
					var next = el.nextElementSibling;
					while (next && !next.classList.contains('hss-result')) {
						next = next.nextElementSibling;
					}
					if (next) next.focus();
				}
				if (e.key === 'ArrowUp') {
					e.preventDefault();
					var prev = el.previousElementSibling;
					while (prev && !prev.classList.contains('hss-result')) {
						prev = prev.previousElementSibling;
					}
					if (prev) prev.focus(); else self.$input.focus();
				}
				if (e.key === 'Escape') {
					self._hideDropdown();
					self.$input.focus();
				}
			});
		});
	};

	/* ── Model preview image loading ─────────────────────────────────── */
	/**
	 * Fills in each rendered .hss-result-thumb with the model's featured
	 * image, fetched from the plugin's REST endpoint (registered by
	 * HSS_Model_Image, which responds with { success, image } — NOT `url`).
	 * Results are cached client-side by slug so repeat keystrokes / re-
	 * renders don't re-fetch the same model twice.
	 */
	var modelImageCache = {};

	HSSBar.prototype._loadModelImages = function () {
		var self = this;
		var cfg  = this.config.modelImage;
		if (!cfg || !cfg.enabled || !cfg.restUrl) return;

		var thumbs = this.$dropdown.querySelectorAll('.hss-result-thumb[data-preview-slug]');

		thumbs.forEach(function (el) {
			var slug = el.dataset.previewSlug;
			if (!slug) return;

			if (Object.prototype.hasOwnProperty.call(modelImageCache, slug)) {
				self._applyModelImage(el, modelImageCache[slug]);
				return;
			}

			fetch(cfg.restUrl + '?slug=' + encodeURIComponent(slug))
				.then(function (r) { return r.json(); })
				.then(function (data) {
					// HSS_Model_Image::get_model_image() returns `image`, not `url`.
					var url = (data && data.success && data.image) ? data.image : (cfg.defaultImage || '');
					modelImageCache[slug] = url;
					self._applyModelImage(el, url);
				})
				.catch(function () {
					var url = cfg.defaultImage || '';
					modelImageCache[slug] = url;
					self._applyModelImage(el, url);
				});
		});
	};

	HSSBar.prototype._applyModelImage = function (el, url) {
		// Element may have been removed from the DOM if the dropdown
		// re-rendered before the fetch resolved — bail quietly.
		if (!el || !el.isConnected) return;
		// No image and no fallback configured: leave the empty gray
		// placeholder box in place rather than collapsing it via
		// display:none — hiding it after the row has already rendered
		// reflows the list right as a user may be about to click it.
		if (!url) return;
		el.innerHTML = '<img src="' + HSSCore.escHtml(url) + '" alt="" loading="lazy">';
	};

	/* ── Result selection ────────────────────────────────────────────── */
	HSSBar.prototype._selectResult = function (el) {
		var type      = el.dataset.type;
		var slug      = el.dataset.slug;
		var name      = el.dataset.name;
		var yearSlug  = el.dataset.yearSlug;
		var yearName  = el.dataset.yearName;
		var modelSlug = el.dataset.modelSlug;
		var modelName = el.dataset.modelName;

		if (type === 'year') {
			state.year  = { slug: slug,      name: name      };
			state.model = null;
			state.trim  = null;
		} else if (type === 'model') {
			state.year  = { slug: yearSlug,  name: yearName  };
			state.model = { slug: slug,      name: name      };
			state.trim  = null;
		} else if (type === 'trim') {
			state.year  = { slug: yearSlug,  name: yearName  };
			state.model = { slug: modelSlug, name: modelName };
			state.trim  = { slug: slug,      name: name      };
		} else if (type === 'feature') {
			// Independent of year/model/trim — doesn't touch those chips.
			state.feature = { slug: slug, name: name };
		}

		this.$input.value = '';
		this._hideDropdown();
		this._renderChips();
		this.$input.focus();
	};

	HSSBar.prototype._hideDropdown = function () {
		this.$dropdown.style.display = 'none';
		this.$dropdown.innerHTML     = '';
	};

	/* ── Smart natural-language parse on Enter / Search click ──────── */
	/**
	 * Tokenises typed input, greedily extracts Year → Model → Trim from
	 * the loaded tree, then checks the leftover words for a feature
	 * category name; whatever's still left after that becomes the
	 * keyword search param.
	 *
	 * Year/Model extraction don't require retyping the level above: if a
	 * Year chip is already active and the user just types a model name,
	 * that still resolves to a Model chip (using the active year as
	 * context) instead of falling through to a plain keyword search —
	 * same for typing just a trim name with Year + Model already active.
	 * Explicitly retyping the year still means "start over": it resets
	 * any existing Model/Trim chip, same as before.
	 */
	HSSBar.prototype._smartParseAndSearch = function (input) {
		if (!input) { this._navigate(); return; }

		// Fix known misspellings/aliases (Settings → Term Corrections)
		// before extracting Year/Model/Trim/Feature, so a corrected term
		// is recognised as a chip rather than falling through to keywords.
		input = HSSCore.applyTermCorrections(input);

		var flat = HSSCore.getFlat();
		var remaining = input;

		/* 1. Extract year (20XX) if typed this time; otherwise fall back
		 * to whatever Year chip is already active. */
		var yearTerm  = null;
		var yearMatch = remaining.match(/\b(20\d{2})\b/);
		if (yearMatch) {
			var yearStr = yearMatch[1];
			yearTerm = flat.years.find(function (y) { return y.name === yearStr; });
			if (yearTerm) {
				state.year  = { slug: yearTerm.slug, name: yearTerm.name };
				state.model = null;
				state.trim  = null;
				remaining   = remaining.replace(yearStr, '').trim();
			}
		} else if (state.year) {
			yearTerm = flat.years.find(function (y) { return y.slug === state.year.slug; });
		}

		/* 2. Extract model (longest-name-first within that year) — runs
		 * whenever a year is known, whether typed just now or already
		 * active from a chip. */
		if (yearTerm) {
			var yearNode = this.tree.find(function (y) { return y.slug === yearTerm.slug; });
			if (yearNode) {
				var modelsSorted = (yearNode.models || []).slice().sort(function (a, b) {
					return b.name.length - a.name.length;
				});

				var modelFound = null;
				for (var mi = 0; mi < modelsSorted.length; mi++) {
					if (remaining.toLowerCase().includes(modelsSorted[mi].name.toLowerCase())) {
						modelFound = modelsSorted[mi];
						break;
					}
				}

				if (modelFound) {
					// A newly-parsed model that differs from whatever's
					// already active invalidates any existing Trim chip
					// — it belonged to the old model. Retyping the SAME
					// model (e.g. as part of a longer phrase alongside a
					// keyword) leaves an existing Trim chip alone.
					if (!state.model || state.model.slug !== modelFound.slug) {
						state.trim = null;
					}
					state.model = { slug: modelFound.slug, name: modelFound.name };
					remaining   = remaining.replace(
						new RegExp(modelFound.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'), ''
					).trim();
				}
			}
		}

		/* 3. Extract trim (longest-name-first within the model) — runs
		 * off whichever model is now known: freshly parsed above, or
		 * already active from a chip, so typing just a trim name (e.g.
		 * "SEL") with Year + Model already selected still narrows it. */
		var trimSourceModel = modelFound
			? modelFound
			: ( state.model ? flat.models.find(function (m) { return m.slug === state.model.slug; }) : null );

		if (trimSourceModel) {
			var trimsSorted = (trimSourceModel.trims || []).slice().sort(function (a, b) {
				return b.name.length - a.name.length;
			});

			var trimFound = null;
			for (var ti = 0; ti < trimsSorted.length; ti++) {
				if (remaining.toLowerCase().includes(trimsSorted[ti].name.toLowerCase())) {
					trimFound = trimsSorted[ti];
					break;
				}
			}

			if (trimFound) {
				state.trim = { slug: trimFound.slug, name: trimFound.name };
				remaining  = remaining.replace(
					new RegExp(trimFound.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'), ''
				).trim();
			}
		}

		/* 4. Extract feature category (longest-name-first, independent of Y/M/T)
		 *    Skipped entirely while feature-matching is disabled in Settings —
		 *    any feature-like words just fall through to keyword search below
		 *    instead of being guessed into a feature_category filter. */
		if (HSSCore.isFeatureMatchingEnabled()) {
			var featuresSorted = flat.features.slice().sort(function (a, b) {
				return b.name.length - a.name.length;
			});
			for (var fi = 0; fi < featuresSorted.length; fi++) {
				if (remaining.toLowerCase().includes(featuresSorted[fi].name.toLowerCase())) {
					state.feature = { slug: featuresSorted[fi].slug, name: featuresSorted[fi].name };
					remaining = remaining.replace(
						new RegExp(featuresSorted[fi].name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'), ''
					).trim();
					break;
				}
			}
		}

		/* 5. Remaining words → keywords */
		remaining = remaining.replace(/\s+/g, ' ').trim();
		if (remaining) {
			state.keywords = remaining;
		}

		this.$input.value = '';
		this._renderChips();
		this._hideDropdown();
		this._navigate();
	};

	/* ── Chip management ─────────────────────────────────────────────── */
	/**
	 * Removing a chip only updates local state + the chip display now —
	 * it deliberately does NOT call _navigate(). This matches how picking
	 * a result from the dropdown already worked (_selectResult never
	 * navigated either) and how the configurator's dropdowns behave:
	 * editing the selection doesn't touch FacetWP/the results until the
	 * user explicitly hits Search (or presses Enter) — both still hit
	 * _navigate() themselves (see _bindEvents' search button handler and
	 * _smartParseAndSearch's empty-input branch).
	 */
	HSSBar.prototype._removeChip = function (type) {
		if      (type === 'year')    { state.year = null; state.model = null; state.trim = null; }
		else if (type === 'model')   { state.model = null; state.trim = null; }
		else if (type === 'trim')    { state.trim  = null; }
		else if (type === 'feature') { state.feature = null; }
		else if (type === 'keyword') { state.keywords = ''; }
		this._renderChips();
	};

	HSSBar.prototype._renderChips = function () {
		var colors = this.config.chipColors;
		var html   = '';
		var hasAny = false;

		function chip(label, type, color) {
			hasAny = true;
			return '<span class="hss-chip" role="listitem" style="background:' + HSSCore.escHtml(color) + '">'
				+ '<span class="hss-chip-label">' + HSSCore.escHtml(label) + '</span>'
				+ '<button class="hss-chip-remove" data-type="' + type + '"'
				+ ' aria-label="Remove ' + HSSCore.escHtml(label) + '">×</button>'
				+ '</span>';
		}

		if (state.year)     html += chip(state.year.name,            'year',    colors.year);
		if (state.model)    html += chip(state.model.name,           'model',   colors.model);
		if (state.trim)     html += chip(state.trim.name,            'trim',    colors.trim);
		if (state.feature)  html += chip(state.feature.name,         'feature', colors.feature);
		if (state.keywords) html += chip('"' + state.keywords + '"', 'keyword', colors.keyword);

		this.$chips.innerHTML              = html;
		this.$clearBtn.style.display       = hasAny ? 'flex' : 'none';
		// Fire integration event — external plugins (e.g. Y-M-T cookie stores)
		// can listen to 'hss:state-changed' on document to react to chip changes.
		this._dispatchChange();
	};

	/**
	 * Clears all chips/keywords and tells FacetWP (and, transitively,
	 * FWPVFM) about it.
	 *
	 * On the actual results page with FacetWP live, the soft FWP.fetch()
	 * path in _navigate() is enough — no full page load happens, so
	 * FWPVFM's own 'facetwp-loaded' listener sees the now-empty URL and
	 * clears its cookie itself, no redirect involved.
	 *
	 * Everywhere else (the common case now that this bar is global), we
	 * still need FWPVFM's cookie cleared server-side — otherwise the next
	 * time this page loads fresh, FWPVFM's own "no filter in URL, but a
	 * saved cookie exists -> reapply it" logic (template_redirect) would
	 * silently bring the old filters back, since we deliberately don't
	 * write/clear that cookie ourselves (see hss-core.js).
	 *
	 * A background fetch() to FWPVFM's ?_clear_vehicle_filter=1 endpoint
	 * (tried in v1.8.6) turned out not to reliably run FWPVFM's clear —
	 * whatever it's doing on template_redirect apparently depends on a
	 * real top-level page load, not just the request reaching the same
	 * URL. So this goes back to a genuine window.location.href navigation
	 * to that endpoint (the only way we've seen the cookie actually get
	 * cleared), but — unlike pre-v1.8.6 — targets wherever the user
	 * currently is instead of config.searchPage, so it reloads in place
	 * instead of yanking them over to the results page.
	 *
	 * state.sort is cleared here too (v1.12) — in the FWP.fetch() branch,
	 * HSSCore.navigate() resets FWP.facets[config.sortFacetName] to []
	 * from the now-null state.sort; in the redirect branch,
	 * buildClearFilterUrl() strips config.paramSort from the target URL.
	 * Either way [hss_video_sort]'s own <select> ends up reset too — the
	 * FWP.fetch() branch via the 'facetwp-loaded' event it listens for,
	 * the redirect branch simply because the page reloads.
	 */
	HSSBar.prototype._clearAll = function () {
		state.year     = null;
		state.model    = null;
		state.trim     = null;
		state.feature  = null;
		state.keywords = '';
		state.sort     = null;
		this.$input.value = '';
		this._renderChips();
		this._hideDropdown();

		if (typeof window.FWP !== 'undefined' && typeof FWP.fetch === 'function') {
			this._navigate();
			return;
		}

		var target    = window.location.pathname + window.location.search;
		var clearUrl  = HSSCore.buildClearFilterUrl(this.config, target);
		window.location.href = clearUrl || target;
	};

	/* ── Integration event ───────────────────────────────────────────── */
	/**
	 * Fires a native CustomEvent ('hss:state-changed') on document every time
	 * the chip state changes. External plugins can listen to this event to
	 * stay in sync without polling — useful for cookie/session stores that
	 * persist Y-M-T selections across visits.
	 *
	 * event.detail shape:
	 *   { year, model, trim, feature }  — each is { slug, name } or null
	 *   keywords               — string
	 *   modelFilter            — comma-joined slug string matching _model_filter param
	 *
	 * Usage in another plugin's JS:
	 *   document.addEventListener('hss:state-changed', function(e) {
	 *       var d = e.detail;
	 *       // d.year?.slug, d.model?.slug, d.trim?.slug, d.feature?.slug, d.keywords
	 *   });
	 */
	HSSBar.prototype._dispatchChange = function () {
		try {
			var slugs = [state.year, state.model, state.trim]
				.filter(Boolean)
				.map(function (x) { return x.slug; });

			document.dispatchEvent(new CustomEvent('hss:state-changed', {
				bubbles: true,
				detail: {
					year:        state.year     || null,
					model:       state.model    || null,
					trim:        state.trim     || null,
					feature:     state.feature  || null,
					keywords:    state.keywords || '',
					sort:        state.sort     || null,
					modelFilter: slugs.join(','),
				},
			}));
		} catch (e) { /* CustomEvent unavailable (very old browser) */ }

		// FWPVFM (FacetWP Vehicle Filter Memory) keeps its own cookie in
		// sync on 'facetwp-loaded' and DOMContentLoaded by reading the URL
		// itself — and _navigate() always updates the URL (pushState or a
		// full redirect) before that fires. So there's nothing to do for
		// year/model/trim here; writing that cookie a second time would
		// just be a second system racing to write the same value FWPVFM
		// already will.
		//
		// Feature Category is different: it's not part of FWPVFM's cookie
		// at all, so it needs its own persistence, and every add/remove/
		// clear of the feature chip already flows through here — one call
		// site covers selecting a feature, removing just that chip, and
		// Clear All (which sets state.feature = null before calling this).
		HSSCore.syncFeatureCookie(state);
	};

	/* ── URL building + navigation — delegates to the shared core ───── */
	HSSBar.prototype._buildUrl = function () {
		return HSSCore.buildUrl(this.config, state);
	};

	HSSBar.prototype._navigate = function () {
		HSSCore.navigate(this.config, state);
	};

	/* ── FacetWP → chip sync ─────────────────────────────────────────── */
	/**
	 * Called on every `facetwp-loaded` event.
	 *
	 * FacetWP has already updated the URL via history.pushState by the time
	 * this fires, so we just re-run HSSCore.parseUrlState() as the source of
	 * truth. We diff against the previous state and only touch the DOM when
	 * something actually changed (avoids flickering when our own _navigate()
	 * was the thing that triggered FWP — chips were already correct).
	 *
	 * Handles:
	 *  - User picks Year/Model/Trim/Feature from the native FWP facets → chips appear
	 *  - User clicks "Reset All Filters" → all chips clear
	 *  - Our own _navigate() fires FWP → no visible change (same state)
	 */
	HSSBar.prototype._syncFromFacet = function () {
		// Snapshot previous state
		var prevYear    = state.year     ? state.year.slug    : null;
		var prevModel   = state.model    ? state.model.slug   : null;
		var prevTrim    = state.trim     ? state.trim.slug    : null;
		var prevFeature = state.feature  ? state.feature.slug : null;
		var prevKw      = state.keywords;

		// Reset and re-parse from the URL FacetWP just updated. Deliberately
		// parseUrlState(), not resolveInitialState() — this handler only
		// ever fires when FacetWP itself is active on the page, which means
		// we're on the results page and the URL FWP just pushed is always
		// the authoritative state, cookie fallback doesn't apply here.
		var fresh = HSSCore.parseUrlState(this.config);
		state.year     = fresh.year;
		state.model    = fresh.model;
		state.trim     = fresh.trim;
		state.feature  = fresh.feature;
		state.keywords = fresh.keywords;
		// Keeps this bar's own copy of state.sort current — e.g. when
		// [hss_video_sort] elsewhere on the page changes sort via its own
		// FWP.fetch() call — so a subsequent Search from this bar carries
		// the sort forward instead of clobbering it with a stale value.
		state.sort     = fresh.sort;

		var newYear    = state.year     ? state.year.slug    : null;
		var newModel   = state.model    ? state.model.slug   : null;
		var newTrim    = state.trim     ? state.trim.slug    : null;
		var newFeature = state.feature  ? state.feature.slug : null;
		var newKw      = state.keywords;

		// Only repaint if something actually changed. Sort intentionally
		// isn't part of this comparison — this bar has no chip/UI for it,
		// so a sort-only change never needs a chip repaint even though
		// state.sort above was still refreshed.
		if ( newYear !== prevYear || newModel !== prevModel ||
		     newTrim !== prevTrim || newFeature !== prevFeature || newKw !== prevKw ) {
			this._renderChips();
		}
	};

	/* ─── Boot ──────────────────────────────────────────────────────── */
	document.addEventListener('DOMContentLoaded', function () {
		if (typeof hssConfig === 'undefined' || typeof hssTree === 'undefined' || typeof window.HSSCore === 'undefined') return;

		// hssConfig.searchPage is localized once, globally, from the admin
		// settings URL — it can't know about a per-shortcode 'search_page'
		// override (wp_enqueue_scripts runs before this shortcode's atts
		// exist). search-bar.php prints that override as data-search-page
		// on #hss-search-wrap when given; only apply it here, on a copy, so
		// the admin-settings value stays the default whenever it's absent.
		var config = hssConfig;
		var $wrap  = document.getElementById('hss-search-wrap');
		if ($wrap && $wrap.dataset.searchPage) {
			config = Object.assign({}, hssConfig, { searchPage: $wrap.dataset.searchPage });
		}

		new HSSBar(config, hssTree).init();
	});

}());
